import { api, useApi, centsInput, toCents, navigate, downloadCsv, today, type Me, type Row } from '../lib';
import { PmSettings } from './Pm';
import { currentMe, setQuery, useLocation } from '../lib';
import { THEMES, applyTheme } from '../theme';
import { BaysSettings } from './Bays';
import { Panel, Loading, ErrorBox, Form, Field, MoneyField, TextArea, Select, useToast, Table, Tabs, SearchBox } from '../ui';
import { fmtTime, dollars } from '../lib';
import { DEFAULT_TIERS, parseTiers, priceFromCost, marginOf, invoiceTotals, totalsOpts, CHARGE_BASES } from '../../shared/calc';
import { useRef, useState } from 'react';
import { Button, Icon, Badge, SearchPick, confirmAction } from '../ui';
import { TrashPanel } from './Trash';
import { logoUrl, LAYOUT_LABELS, INVOICE_STYLES } from '../invoice-sheet';

/** Live example so it's clear what the settings do. */
function SupplyTaxExample({ s }: { s: Record<string, string> }) {
  const t = invoiceTotals([{ description: 'Labor', hours: 400, rate: 12500 }], [{ description: 'Parts', qty: 1, unit_price: 50000, unit_cost: 0 }], totalsOpts(s));
  return <p className="form-note full">Example — $500 labor + $500 parts: shop supplies <b>{dollars(t.supplies)}</b>, sales tax <b>{dollars(t.tax)}</b>, total <b>{dollars(t.total)}</b>. (Updates after you save.)</p>;
}

export function Settings({ me }: { me: Me }) {
  const { data, error, reload } = useApi<Row>('/api/settings');
  const { query } = useLocation();
  const tab = query.get('tab') || 'shop';
  const toast = useToast();
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const pct = (bp: string) => (Number(bp || 0) / 100).toString();
  const toBp = (s: string) => Math.round(Number(s || 0) * 100);
  const TABS = [{ id: 'shop', label: 'Shop & billing' }, { id: 'pricing', label: 'Pricing & invoices' }, { id: 'floor', label: 'Shop floor' }, { id: 'look', label: 'Logo & colors' },
    { id: 'integrations', label: 'Integrations' }, { id: 'activity', label: 'Activity log' }, { id: 'trash', label: 'Trash' }, { id: 'account', label: 'My account' }];
  return (
    <>
      <div className="page-head"><div><h1>Settings</h1><p>Shop details printed on invoices, rates and numbering.</p></div>
        <div className="actions"><Button icon="download" onClick={() => navigate('/import')}>Import data (Fullbay…)</Button></div></div>
      <Tabs value={tab} onChange={t => setQuery({ tab: t })} tabs={TABS} />
      {tab === 'shop' && <div className="grid-2">
        <Panel title="Shop & billing">
          <Form submitLabel="Save settings" onSubmit={async v => {
            await api('/api/settings', { method: 'PATCH', body: {
              shop_name: v.shop_name, shop_phone: v.shop_phone, shop_address: v.shop_address, shop_email: v.shop_email, invoice_footer: v.invoice_footer, payment_instructions: v.payment_instructions,
              labor_rate: toCents(v.labor_rate), parts_tax_bp: toBp(v.parts_tax), supplies_bp: toBp(v.supplies), tax_on: v.tax_on, supplies_on: v.supplies_on, tire_disposal_fee: toCents(v.tire_disposal_fee || '0'), supplies_max: toCents(v.supplies_max || '0'), tax_supplies: v.tax_supplies === 'on' ? 1 : 0, default_terms: Number(v.default_terms || 0),
              overtime: v.overtime === 'on' ? 1 : 0, next_wo: Number(v.next_wo), next_invoice: Number(v.next_invoice),
              work_days: Number(v.work_days), shift_start: v.shift_start, shift_end: v.shift_end, extra_rate_pct: Number(v.extra_rate_pct),
              late_deducts: v.late_deducts === 'on' ? 1 : 0, absent_deducts: v.absent_deducts === 'on' ? 1 : 0, early_deducts: v.early_deducts === 'on' ? 1 : 0,
              payment_methods: String(v.payment_methods || '').split('\n').map(x => x.trim()).filter(Boolean),
            } });
            const me = currentMe(); if (me) me.shop.payment_methods = JSON.stringify([...new Set(String(v.payment_methods || '').split('\n').map(x => x.trim()).filter(Boolean))]);
            toast('Settings saved'); reload();
          }}>
            <Field label="Shop name" name="shop_name" defaultValue={data.shop_name} required full />
            <Field label="Address" name="shop_address" defaultValue={data.shop_address} full />
            <Field label="Phone" name="shop_phone" defaultValue={data.shop_phone} />
            <Field label="Email" name="shop_email" type="email" defaultValue={data.shop_email} />
            <MoneyField label="Default labor rate / hour" name="labor_rate" defaultValue={centsInput(Number(data.labor_rate))} />
            <Field label="Default payment terms (days)" name="default_terms" type="number" min={0} defaultValue={data.default_terms} />
            <h4 className="form-sub full">Shop supplies & sales tax</h4>
            <Field label="Shop supplies (%)" name="supplies" type="number" step="0.1" min={0} max={30} defaultValue={pct(data.supplies_bp)} hint="0 = don't charge shop supplies" />
            <Select label="Shop supplies % is taken from" name="supplies_on" defaultValue={data.supplies_on || 'labor'}>{CHARGE_BASES.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}</Select>
            <MoneyField label="Shop supplies max per invoice" name="supplies_max" defaultValue={Number(data.supplies_max) ? centsInput(Number(data.supplies_max)) : ''} hint="Leave empty for no cap" />
            <span />
            <Field label="Sales tax (%)" name="parts_tax" type="number" step="0.01" min={0} max={30} defaultValue={pct(data.parts_tax_bp)} hint="0 if your customers are tax-exempt / you don't charge it" />
            <Select label="Sales tax is charged on" name="tax_on" defaultValue={data.tax_on || 'parts'}>{CHARGE_BASES.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}</Select>
            <label className="check full"><input type="checkbox" name="tax_supplies" defaultChecked={data.tax_supplies === '1'} /> Charge sales tax on shop supplies too</label>
            <SupplyTaxExample s={data} />
            <MoneyField label="Tire disposal fee (per tire)" name="tire_disposal_fee" defaultValue={centsInput(Number(data.tire_disposal_fee || 0)) || '0'} hint="Added as a fee line whenever tires go on a job. 0 = don't add. You can change or delete it on any job." />
            <h4 className="form-sub full">Numbering</h4>
            <Field label="Next work order #" name="next_wo" type="number" min={1} defaultValue={data.next_wo} />
            <Field label="Next invoice #" name="next_invoice" type="number" min={1} defaultValue={data.next_invoice} />
            <label className="check full"><input type="checkbox" name="overtime" defaultChecked={data.overtime === '1'} /> Pay hourly staff 1.5× over 40 hours a week</label>
            <h4 className="form-sub full">Work schedule & attendance</h4>
            <Select label="Work days" name="work_days" defaultValue={data.work_days || '6'}><option value="5">Mon–Fri</option><option value="6">Mon–Sat</option><option value="7">Every day</option></Select>
            <Select label="Extra hours paid at" name="extra_rate_pct" defaultValue={data.extra_rate_pct || '100'}><option value="100">Normal hourly rate</option><option value="150">1.5× (time and a half)</option></Select>
            <Field label="Shift starts" name="shift_start" type="time" defaultValue={data.shift_start || '09:00'} required />
            <Field label="Shift ends" name="shift_end" type="time" defaultValue={data.shift_end || '19:00'} required />
            <label className="check full"><input type="checkbox" name="late_deducts" defaultChecked={data.late_deducts !== '0'} /> Deduct late minutes from salaried staff</label>
            <label className="check full"><input type="checkbox" name="absent_deducts" defaultChecked={data.absent_deducts === '1'} /> Deduct a day's pay when salaried staff are absent without permission</label>
            <label className="check full"><input type="checkbox" name="early_deducts" defaultChecked={data.early_deducts === '1'} /> Deduct the minutes when salaried staff leave early <b>on their own</b> (leaving early with your permission never costs them)</label>
            <TextArea label="How customers can pay (shown on invoices, emails and the portal)" name="payment_instructions" defaultValue={data.payment_instructions} />
            <TextArea label="Payment methods — one per line (the list when you record a payment)" name="payment_methods" rows={6}
              defaultValue={(() => { try { return (JSON.parse(data.payment_methods || '[]') as string[]).join('\n'); } catch { return ''; } })() || 'Cash\nCheck\nCard\nACH\nZelle\nOther'}
              placeholder={'Cash\nCheck\nCard\nACH\nZelle\nWire\nAccount transfer\nEFS\nOther'} />
            <TextArea label="Invoice footer" name="invoice_footer" defaultValue={data.invoice_footer} />
          </Form>
        </Panel>
        <div className="stack">
          <RoadService data={data} onSaved={reload} />
          <BodyShop data={data} onSaved={reload} />
        </div>
      </div>}
      {tab === 'pricing' && <div className="grid-2">
        <div className="stack">
          <PartsPricing data={data} onSaved={reload} />
          <SpecialPricing />
          <PmSettings />
        </div>
        <div className="stack">
          <InvoiceStyle data={data} onSaved={reload} />
          <InvoiceRules data={data} onSaved={reload} />
          <Panel title="Invoices & estimates">
            <Form submitLabel="Save invoice settings" onSubmit={async v => {
              await api('/api/settings', { method: 'PATCH', body: { estimate_valid_days: Number(v.estimate_valid_days || 0), estimate_terms: v.estimate_terms, invoice_terms: v.invoice_terms, invoice_layout: v.invoice_layout, ai_auto_wording: v.ai_auto_wording === 'on' ? 1 : 0 } });
              toast('Saved'); reload();
            }}>
              <Select label="Default invoice layout" name="invoice_layout" defaultValue={data.invoice_layout || 'by_job'} full
                hint="You can switch the layout on any invoice or estimate with one click — this is just where it starts.">
                {Object.entries(LAYOUT_LABELS).map(([k, [name, d]]) => <option key={k} value={k}>{name} — {d}</option>)}
              </Select>
              <label className="check full"><input type="checkbox" name="ai_auto_wording" defaultChecked={data.ai_auto_wording === '1'} /> AI fixes labor wording automatically when a line is saved (any language → shop English)</label>
              <Select label="Estimates are valid for" name="estimate_valid_days" defaultValue={data.estimate_valid_days || '0'} full>
                <option value="0">Today only</option><option value="3">3 days</option><option value="7">7 days</option><option value="15">15 days</option><option value="30">30 days</option>
              </Select>
              <TextArea label="Estimate terms (one per line)" name="estimate_terms" rows={6} defaultValue={data.estimate_terms} />
              <TextArea label="Invoice terms (one per line)" name="invoice_terms" rows={8} defaultValue={data.invoice_terms} />
              <p className="form-note full">{'{shop}'} is replaced with your shop name. Invoices keep the terms they were issued with. These are common shop terms, not legal advice — have your attorney or insurance agent look them over once.</p>
            </Form>
          </Panel>
          <PartsRunSettings data={data} onSaved={reload} />
        </div>
      </div>}
      {tab === 'floor' && <div className="grid-2">
        <div className="stack">
          <Panel title="Shop floor (mechanics)">
            <Form submitLabel="Save" onSubmit={async v => {
              await api('/api/settings', { method: 'PATCH', body: { auto_grease: v.auto_grease === 'on' ? 1 : 0, auto_grease_qty: Math.max(1, Number(v.auto_grease_qty || 2)), auto_grease_part: v.auto_grease_part || '', ai_daily_limit: Math.max(0, Number(v.ai_daily_limit || 0)), supervisor_on: v.supervisor_on === 'on' ? 1 : 0, supervisor_block_invoice: v.supervisor_block_invoice === 'on' ? 1 : 0, comeback_days: Number(v.comeback_days || 90), return_alert_days: Number(v.return_alert_days || 14) } });
              { const me = currentMe(); if (me) me.shop.return_alert_days = String(v.return_alert_days || 14); }
              const shots = String(v.arrival_shots || '').split('\n').map(x => x.trim()).filter(Boolean);
              await api('/api/settings', { method: 'PATCH', body: { arrival_shots: shots } });
              const me = currentMe(); if (me) { me.shop.supervisor_on = v.supervisor_on === 'on' ? '1' : '0'; me.shop.arrival_shots = JSON.stringify(shots); }
              toast('Saved'); reload();
            }}>
              <div className="note-bar full"><Icon name="users" size={16} /><span className="grow">Who can do what on the phone — see only their jobs, check trucks in, add companies, give out or take over jobs, parts, receipts — is set per role.</span>
                <Button onClick={() => navigate('/staff?tab=roles')}>Staff → Roles</Button></div>
              <label className="field full"><span>Comebacks (warranty redo) — look back</span>
                <select name="comeback_days" defaultValue={data.comeback_days || '90'}>
                  {[['30', '30 days'], ['60', '60 days'], ['90', '90 days'], ['180', '6 months'], ['365', '1 year'], ['730', '2 years'], ['1095', '3 years'], ['1825', '5 years']].map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
                <small>A new job on a truck that had the same kind of work in this time asks "Comeback?" — warranty, no charge, and it counts against the mechanic in Team performance.</small></label>
              <label className="field full"><span>Parts to return to vendors — warn after</span>
                <select name="return_alert_days" defaultValue={data.return_alert_days || '14'}>
                  {[['7', '7 days'], ['14', '14 days'], ['21', '21 days'], ['30', '30 days']].map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
                <small>Defective, wrong or not-needed parts (and cores) waiting longer turn red on the Dashboard and Inventory → Returns.</small></label>
              <label className="check full"><input type="checkbox" name="auto_grease" defaultChecked={data.auto_grease !== '0'} /> Oil change / PM jobs get grease added by themselves (delete it on a job if not used)</label>
              <Field label="Grease quantity" name="auto_grease_qty" type="number" min={1} defaultValue={data.auto_grease_qty || '2'} hint="e.g. 2 (quarts)" />
              <GreasePart value={data.auto_grease_part || ''} />
              <h4 className="form-sub full">Supervisor check</h4>
              <label className="check full"><input type="checkbox" name="supervisor_on" defaultChecked={data.supervisor_on === '1'} /> A finished job waits for the supervisor: <b>✓ Checked OK</b> or <b>Redo</b> with a note back to the mechanic. Supervisors: give someone the "Shop foreman (supervisor)" role (Staff → Roles), or tick "Supervisor" on any role.</label>
              <label className="check full"><input type="checkbox" name="supervisor_block_invoice" defaultChecked={data.supervisor_block_invoice === '1'} /> Don't invoice a work order until every finished job is checked</label>
              <h4 className="form-sub full">Arrival photos (check-in walk-around)</h4>
              <TextArea label="Pictures the mechanic takes when a truck comes in — one per line" name="arrival_shots" rows={6} full
                defaultValue={(() => { try { return (JSON.parse(data.arrival_shots || '[]') as string[]).join('\n'); } catch { return ''; } })()} />
              <p className="form-note full">Date, time and mileage are printed on each arrival picture. Empty = no checklist.</p>
              <Field label="AI photo reads per day (VIN, company, part label)" name="ai_daily_limit" type="number" min={0} defaultValue={data.ai_daily_limit || '300'} hint="About 0.2¢ each. 0 = no limit." full />
            </Form>
          </Panel>
        </div>
        <div className="stack">
          <ShopTvPanel />
          <BaysSettings />
          <Panel title="Outside techs (specialists)"><p className="pad">Outside techs, what you owe them and their documents are in <a href="/staff?tab=outside" onClick={e => { e.preventDefault(); navigate('/staff?tab=outside'); }}>Staff → Outside techs</a>.</p></Panel>
        </div>
      </div>}
      {tab === 'look' && <div className="grid-2">
        <div className="stack"><Branding version={data.logo_version} onChange={reload} /></div>
        <div className="stack"><Colors current={data.theme || 'blue'} onSaved={reload} /></div>
      </div>}
      {tab === 'integrations' && <div className="grid-2">
        <EmailStatus />
        <TelegramSetup />
        <Trackers />
        <QuickBooks />
        <YourData />
      </div>}
      {tab === 'activity' && <ActivityLog />}
      {tab === 'trash' && <TrashPanel />}
      {tab === 'account' && <div className="grid-2"><div className="stack">
          <Panel title="My password">
            <Form submitLabel="Change password" onSubmit={async (v, f) => {
              if (v.password !== v.password2) throw new Error('New passwords do not match');
              await api('/api/me/password', { body: v }); f.reset(); toast('Password changed');
            }}>
              <Field label="Current password" name="current" type="password" required full autoComplete="current-password" />
              <Field label="New password" name="password" type="password" minLength={6} required autoComplete="new-password" />
              <Field label="Repeat new password" name="password2" type="password" minLength={6} required autoComplete="new-password" />
            </Form>
            <p className="form-note pad">Signed in as <b>{me.user.login}</b>.</p>
          </Panel>
      </div></div>}
    </>
  );
}

/** Everything that happened, newest first — with a search. */
function ActivityLog() {
  const activity = useApi<Row[]>('/api/activity');
  const [q, setQ] = useState('');
  const [n, setN] = useState(200);
  const k = q.trim().toLowerCase();
  const rows = (activity.data || []).filter(a => !k || `${a.name} ${a.action}`.toLowerCase().includes(k));
  return (
    <>
      <div className="toolbar"><SearchBox value={q} onChange={setQ} placeholder="Search — a name, WO #, invoice #, part…" /><span className="spacer" /><span className="muted">{rows.length} entries</span></div>
      <Panel>
        {!activity.data ? <Loading /> : <Table heads={['When', 'Who', 'What']} empty="No activity yet."
          rows={rows.slice(0, n).map(a => [<small>{fmtTime(a.created_at)}</small>, a.name || '—', a.action])} />}
        {rows.length > n && <div className="pad"><Button onClick={() => setN(n + 300)}>Show more</Button></div>}
      </Panel>
    </>
  );
}

function Branding({ version, onChange }: { version: string; onChange: () => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  async function upload(f?: File | null) {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.set('file', f);
      await api('/api/branding/logo', { body: fd }); toast('Logo updated'); onChange();
      setTimeout(() => location.reload(), 600);
    } catch (e) { toast((e as Error).message, 'err'); } finally { setBusy(false); if (ref.current) ref.current.value = ''; }
  }
  return (
    <Panel title="Logo" action={<div className="actions">
      <Button icon="image" busy={busy} onClick={() => ref.current?.click()}>Upload new logo</Button>
      {version !== '0' && <Button kind="ghost" onClick={async () => { await api('/api/branding/logo', { method: 'DELETE' }); onChange(); location.reload(); }}>Use default</Button>}
    </div>}>
      <input ref={ref} type="file" accept="image/png,image/jpeg" hidden onChange={e => void upload(e.target.files?.[0])} />
      <div className="logo-preview"><img src={logoUrl(version)} alt="Shop logo" /></div>
      <p className="form-note pad">Shown on your invoices, estimates, emails, the customer portal and at the top of the app. Use a wide PNG or JPG on a white background, under 2 MB.</p>
    </Panel>
  );
}

function EmailStatus() {
  const { data } = useApi<{ configured: boolean; from: string }>('/api/email-status');
  if (!data) return null;
  return (
    <Panel title="Email & customer portal">
      <p className="pad">{data.configured
        ? <><Badge tone="green">On</Badge> Invoices and statements are sent from <b>{data.from}</b>.</>
        : <><Badge tone="amber">Not set up yet</Badge> Asena Bay will turn on email for all shops soon. Until then, copy links (invoices, estimates, parts orders) and send them yourself. Replies always go to your shop email above.</>}</p>
      <p className="form-note pad">Customer portal address: <b>{location.origin}/portal</b> <Icon name="link" size={14} /></p>
    </Panel>
  );
}

/** Parts markup: off, one % for everything, or a matrix by cost range — like Fullbay's markup matrix. */
/** Road service defaults: call fee, hourly rate, minimum hours and the note printed on road estimates/invoices. */
/** Telegram bot for this shop: make one with @BotFather, paste the token here. */
function TelegramSetup() {
  const { data, reload } = useApi<Row>('/api/telegram');
  const shopName = String(currentMe()?.shop.shop_name || 'ABC Truck Repair');
  const botName = shopName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').split('_').slice(0, 2).join('_') + '_bot';
  const toast = useToast();
  return (
    <Panel title="Telegram">
      {!data ? <Loading /> : data.connected ? (
        <div className="pad">
          <p>✅ Connected: <a href={`https://t.me/${data.bot}`} target="_blank" rel="noreferrer"><b>@{data.bot}</b></a>. Invoices and estimates go to customers who connected (Customer page → Telegram) — to a person, or to the customer's group.</p>
          <h4 className="sub-h">Your shop's group (copy of every new invoice)</h4>
          {data.shop_group_customer && <div className="error-box"><Icon name="alert" />The shop group <b>{data.shop_group}</b> is also connected to the customer <b>{data.shop_group_customer}</b> — so new invoices are NOT posted there (other customers would see them). Disconnect it and add the bot to a group with only your staff.</div>}
          {data.shop_group ? <p>✅ Posting new invoices to <b>{data.shop_group}</b>. <button type="button" className="linkish" onClick={async () => { await api('/api/telegram/shop-group', { method: 'DELETE' }); toast('Shop group disconnected'); reload(); }}>Disconnect</button></p>
            : data.shop_group_link ? <>
              <p className="form-note">Open this link on a phone with Telegram, pick your group (you must be its admin) and press <b>Add bot</b> / <b>Start</b>. From then on every new invoice is posted there.</p>
              <div className="actions-row"><Button onClick={() => window.open(data.shop_group_link, '_blank')}>Add bot to our group</Button>
                <Button kind="ghost" onClick={async () => { await navigator.clipboard?.writeText(data.shop_group_link); toast('Link copied'); }}>Copy link</Button></div></> : null}
          <div className="actions-row tg-off"><Button kind="danger" onClick={async () => { await api('/api/telegram', { method: 'DELETE' }); toast('Telegram disconnected'); reload(); }}>Disconnect bot</Button></div>
        </div>
      ) : (
        <Form submitLabel="Connect bot" onSubmit={async v => { const r = await api<{ bot: string }>('/api/telegram', { body: { token: v.token } }); toast(`Connected @${r.bot}`); reload(); }}>
          <ol className="form-note full tg-steps">
            <li>In Telegram, open <b>@BotFather</b> → <b>/newbot</b>. Name it after the shop (e.g. "{shopName}"), username like <b>{botName}</b>.</li>
            <li>BotFather sends a token like <code>123456789:AAE…</code>. Paste it below.</li>
            <li>Then on each customer's page send them their Telegram link once — after they tap <b>Start</b>, invoices and estimates go straight to them.</li>
          </ol>
          <Field label="Bot token" name="token" required full placeholder="123456789:AAE..." autoComplete="off" />
        </Form>
      )}
    </Panel>
  );
}

/** Chosen customers (your own fleet, a good fleet) get parts at cost + their own %; everyone else keeps the shop markup. */
function SpecialPricing() {
  const customers = useApi<Row[]>('/api/customers');
  const [pick, setPick] = useState('');
  const [pct, setPct] = useState('');
  const toast = useToast();
  const list = (customers.data || []).filter(c => c.parts_markup !== null && c.parts_markup !== undefined);
  const set = async (id: string, v: number | null) => {
    try { await api(`/api/customers/${id}/markup`, { method: 'PUT', body: { pct: v } }); customers.reload(); toast(v === null ? 'Back to normal pricing' : 'Special pricing saved'); }
    catch (e) { toast((e as Error).message, 'err'); }
  };
  return (
    <Panel title="Special parts pricing for customers">
      <div className="pad">
        <p className="form-note">Parts on these customers' jobs are priced <b>cost + their %</b> (instead of the inventory price or the markup above). Everyone else keeps the normal pricing. Customers never see this.</p>
        {list.length > 0 && <Table heads={['Customer', 'Parts markup', '']} rows={list.map(c => [<b>{c.name}</b>,
          <span className="affix small-affix"><input type="number" min={0} max={1000} defaultValue={c.parts_markup} aria-label={`Markup for ${c.name}`} onBlur={e => { const n = Number(e.target.value); if (e.target.value !== '' && n !== c.parts_markup) void set(c.id, n); }} /><em>%</em></span>,
          <Button kind="ghost" onClick={() => set(c.id, null)}>Remove</Button>])} />}
        <div className="special-add">
          <SearchPick label="Add a customer" full={false} value={pick} onChange={setPick} placeholder="Type the customer name"
            items={(customers.data || []).filter(c => c.parts_markup === null || c.parts_markup === undefined).map(c => ({ id: c.id, label: c.name, sub: c.usdot ? `USDOT ${c.usdot}` : undefined }))} />
          <label className="field"><span>Parts markup %</span><input type="number" min={0} max={1000} value={pct} onChange={e => setPct(e.target.value)} placeholder="e.g. 10" /></label>
          <Button kind="primary" disabled={!pick || pct === ''} onClick={async () => { await set(pick, Number(pct)); setPick(''); setPct(''); }}>Add</Button>
        </div>
      </div>
    </Panel>
  );
}

/** Invoice / estimate header style — the shop picks one of four. */
const STYLE_DEMO: Record<string, [number, number, number, number, string?][]> = {
  classic: [[6, 8, 30, 14], [6, 26, 40, 4], [70, 8, 24, 6], [70, 18, 18, 4], [6, 38, 30, 4], [52, 38, 30, 4]],
  right: [[6, 8, 22, 6], [6, 18, 16, 4], [68, 6, 26, 16], [6, 34, 30, 4], [52, 34, 30, 4], [52, 42, 24, 4]],
  center: [[38, 4, 24, 14], [30, 21, 40, 3], [4, 28, 92, 8, '#cbd5e1'], [6, 42, 30, 4], [52, 42, 30, 4]],
  band: [[0, 0, 100, 24, '#1e3a5f'], [5, 4, 24, 16, '#fff'], [72, 6, 22, 5, '#fff'], [6, 34, 24, 4], [38, 34, 24, 4], [70, 34, 24, 4]],
};
function InvoiceStyle({ data, onSaved }: { data: Row; onSaved: () => void }) {
  const toast = useToast();
  const cur = data.invoice_style || 'classic';
  return (
    <Panel title="Invoice style">
      <div className="pad">
        <div className="style-pick">{INVOICE_STYLES.map(([k, name, d]) => (
          <button key={k} type="button" className={cur === k ? 'on' : ''} onClick={async () => { await api('/api/settings', { method: 'PATCH', body: { invoice_style: k } }); toast(`Invoice style: ${name}`); onSaved(); }}>
            <div className="sp-demo">{STYLE_DEMO[k].map(([x, y, w, h, bg], i) => <i key={i} style={{ left: x + '%', top: y, width: w + '%', height: h, ...(bg ? { background: bg } : {}) }} />)}</div>
            <b>{name}{cur === k ? ' ✓' : ''}</b><br /><small className="muted">{d}</small></button>))}</div>
        <p className="form-note">The top of every invoice and estimate (print, PDF, email link and the customer portal). The table layout is picked separately below.</p>
      </div>
    </Panel>
  );
}

function InvoiceRules({ data, onSaved }: { data: Row; onSaved: () => void }) {
  const toast = useToast();
  return (
    <Panel title="Invoices, discounts & declined work">
      <Form submitLabel="Save" onSubmit={async v => {
        await api('/api/settings', { method: 'PATCH', body: {
          overdue_grace: Number(v.overdue_grace || 0), tag_lines: v.tag_lines === 'on' ? 1 : 0, disc_reason: v.disc_reason === 'on' ? 1 : 0,
          disclaimer_on: v.disclaimer_on === 'on' ? 1 : 0, invoice_disclaimer: v.invoice_disclaimer, advised_photos_default: v.advised_photos_default === 'on' ? 1 : 0,
          part_numbers: v.part_numbers === 'on' ? 'show' : 'hide',
        } });
        toast('Saved'); onSaved();
      }}>
        <Field label="Overdue after (days past due)" name="overdue_grace" type="number" min={0} max={90} defaultValue={data.overdue_grace ?? '7'} hint="An unpaid invoice shows as Overdue only this many days after its due date" />
        <span />
        <label className="check full"><input type="checkbox" name="tag_lines" defaultChecked={data.tag_lines !== '0'} /> Show LABOR / PART / FEE tags on invoice lines (labor on its own line under each job)</label>
        <label className="check full"><input type="checkbox" name="part_numbers" defaultChecked={data.part_numbers === 'show'} /> Show part numbers on estimates and invoices (off: the customer sees the part name, qty and price; turn it on for one document on its page)</label>
        <label className="check full"><input type="checkbox" name="disc_reason" defaultChecked={data.disc_reason === '1'} /> A reason is required for every discount</label>
        <p className="form-note full">Who may give discounts, and how much: <b>Staff & Payroll</b> → edit the person → Discounts. Over their limit, the discount waits for your OK.</p>
        <label className="check full"><input type="checkbox" name="disclaimer_on" defaultChecked={data.disclaimer_on !== '0'} /> Print the "work performed" note on every invoice</label>
        <TextArea label="Work performed note ({shop} = your shop name)" name="invoice_disclaimer" defaultValue={data.invoice_disclaimer} />
        <label className="check full"><input type="checkbox" name="advised_photos_default" defaultChecked={data.advised_photos_default === '1'} /> Photos on "advised — customer declined" items: show to the customer by default</label>
      </Form>
    </Panel>
  );
}

function PartsRunSettings({ data, onSaved }: { data: Row; onSaved: () => void }) {
  const toast = useToast();
  return (
    <Panel title="Parts runs">
      <Form submitLabel="Save parts runs" onSubmit={async v => {
        await api('/api/settings', { method: 'PATCH', body: { run_default: v.run_default, run_flat: toCents(v.run_flat || '0'), run_rate: toCents(v.run_rate || '0') } });
        toast('Saved'); onSaved();
      }}>
        <Select label="When someone is back from a parts run" name="run_default" defaultValue={data.run_default || 'internal'}>
          <option value="internal">Internal only — customer never sees it</option><option value="free">Show on the invoice at no charge</option><option value="charge">Charge the customer</option>
        </Select>
        <span />
        <MoneyField label="Charge: flat price per run" name="run_flat" defaultValue={centsInput(Number(data.run_flat || 0))} hint="Used when you charge a flat price" />
        <MoneyField label="Charge: per hour instead" name="run_rate" defaultValue={Number(data.run_rate) ? centsInput(Number(data.run_rate)) : ''} hint="Fill in to charge by the hour (leave empty for flat)" />
        <p className="form-note full">The office can still change each run (charge / no charge / internal). Owner report: Reports → Parts runs.</p>
      </Form>
    </Panel>
  );
}

/** Body shop: turn body work on (picture picker on work orders, estimates, quick jobs and the mechanic app) and its prices. */
function BodyShop({ data, onSaved }: { data: Row; onSaved: () => void }) {
  const toast = useToast();
  const [on, setOn] = useState(data.body_on === '1');
  return (
    <Panel title="Body shop">
      <Form submitLabel="Save body shop" onSubmit={async v => {
        await api('/api/settings', { method: 'PATCH', body: on ? { body_on: 1, body_rate: toCents(v.body_rate || '0'), paint_rate: toCents(v.paint_rate || '0'), paint_mat: toCents(v.paint_mat || '0'), haz_fee: toCents(v.haz_fee || '0') } : { body_on: 0 } });
        const me = currentMe(); if (me) me.shop.body_on = on ? '1' : '0';
        toast(on ? 'Body work is on' : 'Body work is off'); onSaved();
      }}>
        <label className="check full"><input type="checkbox" checked={on} onChange={e => setOn(e.target.checked)} /> Body work on — a <b>Body work</b> button on work orders, estimates and quick jobs, and <b>Body damage</b> on the mechanic's phone (tap the damaged parts on a truck / trailer picture)</label>
        {on && <>
          <MoneyField label="Body labor rate / hour" name="body_rate" defaultValue={centsInput(Number(data.body_rate || 0))} hint="Empty = your normal labor rate" />
          <MoneyField label="Paint labor rate / hour" name="paint_rate" defaultValue={centsInput(Number(data.paint_rate || 0))} hint="Empty = the body rate" />
          <MoneyField label="Paint & materials per paint hour" name="paint_mat" defaultValue={centsInput(Number(data.paint_mat ?? 4800))} hint="Primer, sealer, base, clear, sandpaper, tape — added as one line by paint hours. 0 = don't add" />
          <MoneyField label="Hazardous waste fee (paint jobs)" name="haz_fee" defaultValue={centsInput(Number(data.haz_fee ?? 3500))} hint="Paint / solvent disposal, once per body job with paint. 0 = don't add" />
          <p className="form-note full">Hours come from a starting guide (repair light / medium / heavy, replace, remove & reinstall, paint, blend). Change any line on the job. A body job can go on its own invoice when you add it, or later with <b>Move body work to its own invoice</b>.</p>
        </>}
      </Form>
    </Panel>
  );
}

function RoadService({ data, onSaved }: { data: Row; onSaved: () => void }) {
  const toast = useToast();
  return (
    <Panel title="Road service">
      <Form submitLabel="Save road service" onSubmit={async v => {
        await api('/api/settings', { method: 'PATCH', body: { road_call_fee: toCents(v.road_call_fee), road_rate: toCents(v.road_rate), road_min_hours: Math.round(Number(v.road_min_hours || 0) * 100), road_terms: v.road_terms } });
        toast('Road service saved'); onSaved();
      }}>
        <MoneyField label="Call fee" name="road_call_fee" defaultValue={centsInput(Number(data.road_call_fee || 0))} hint="Added to every road service estimate / job" />
        <MoneyField label="Road labor rate / hour" name="road_rate" defaultValue={centsInput(Number(data.road_rate || 0))} />
        <Field label="Minimum hours" name="road_min_hours" type="number" step="0.25" min={0} defaultValue={String(Number(data.road_min_hours || 0) / 100)} hint="Billed even if the call is shorter" />
        <TextArea label="Note printed on road estimates & invoices (one per line)" name="road_terms" defaultValue={data.road_terms} />
        <p className="form-note full">The clock starts when someone presses <b>Leave facility</b> (office or phone) and stops at <b>Back at shop — done</b>; hours are filled in automatically and can still be changed by hand.</p>
      </Form>
    </Panel>
  );
}

function PartsPricing({ data, onSaved }: { data: Row; onSaved: () => void }) {
  const [mode, setMode] = useState<string>(data.parts_markup_mode || 'flat');
  const [flat, setFlat] = useState<string>(String(data.parts_markup_pct || '0'));
  const [rows, setRows] = useState<{ upto: string; pct: string }[]>(() => {
    const t = parseTiers(data.parts_markup_tiers);
    return (t.length ? t : DEFAULT_TIERS).map(x => ({ upto: x.upto === null ? '' : centsInput(x.upto), pct: String(x.pct) }));
  });
  const toast = useToast();
  const set = (i: number, k: 'upto' | 'pct', v: string) => setRows(r => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const example = (cost: number) => { const p = priceFromCost(cost, { parts_markup_mode: mode, parts_markup_pct: flat, parts_markup_tiers: JSON.stringify(tiers()) }); return p ? `${dollars(cost)} → ${dollars(p)} (margin ${marginOf(cost, p)}%)` : 'type the price yourself'; };
  function tiers() {
    return rows.filter(r => r.pct !== '').map((r, i) => ({ upto: r.upto === '' || i === rows.length - 1 ? null : toCents(r.upto), pct: Number(r.pct) || 0 }));
  }
  return (
    <Panel title="Parts pricing (markup)">
      <div className="form"><div className="form-body">
        <p className="form-note full"><b>Markup</b> is profit as a % of what the part <i>cost you</i>: cost $100 + 50% markup = sell $150. <b>Margin</b> is the same profit as a % of the <i>sell price</i>: $50 ÷ $150 = 33%. Markup is always the bigger number.</p>
        <div className="seg-choice three full">
          <button type="button" className={mode === 'off' ? 'on' : ''} onClick={() => setMode('off')}>Off — I type prices</button>
          <button type="button" className={mode === 'flat' ? 'on' : ''} onClick={() => setMode('flat')}>One % for all</button>
          <button type="button" className={mode === 'tiers' ? 'on' : ''} onClick={() => setMode('tiers')}>By cost range</button>
        </div>
        {mode === 'flat' && <Field label="Markup %" name="_flat" type="number" min={0} max={1000} value={flat} onChange={e => setFlat(e.target.value)} />}
        {mode === 'tiers' && (
          <div className="full">
            <table className="tiers"><thead><tr><th>Part cost from</th><th>up to</th><th>Markup %</th><th>Example</th><th /></tr></thead>
              <tbody>{rows.map((r, i) => {
                const from = i === 0 ? '$0.00' : rows[i - 1].upto ? `$${rows[i - 1].upto}` : '—';
                const last = i === rows.length - 1;
                const sample = toCents(r.upto || '0') || (toCents(rows[i - 1]?.upto || '0') * 2) || 50000;
                return (
                  <tr key={i}><td className="muted">{from}</td>
                    <td>{last ? <span className="muted">and up</span> : <span className="pe-money"><em>$</em><input value={r.upto} onChange={e => set(i, 'upto', e.target.value)} inputMode="decimal" /></span>}</td>
                    <td><input value={r.pct} onChange={e => set(i, 'pct', e.target.value)} inputMode="numeric" className="num" /> %</td>
                    <td className="muted small">{example(sample)}</td>
                    <td>{rows.length > 1 && <button type="button" className="icon-btn" title="Remove" onClick={() => setRows(x => x.filter((_, j) => j !== i))}><Icon name="trash" size={15} /></button>}</td></tr>
                );
              })}</tbody></table>
            <div className="actions-row"><button type="button" className="linkish" onClick={() => setRows(x => [...x.slice(0, -1), { upto: '', pct: x[x.length - 1]?.pct || '30' }, x[x.length - 1]])}>+ Add a range</button>
              <button type="button" className="linkish" onClick={() => setRows(DEFAULT_TIERS.map(x => ({ upto: x.upto === null ? '' : centsInput(x.upto), pct: String(x.pct) })))}>Reset to example</button></div>
          </div>
        )}
        <p className="form-note full">Used when you type a cost without a price (parts typed into a job, parts received on an order that aren't in the catalog yet). You can always change the price on any line.</p>
      </div>
        <div className="form-foot"><span className="spacer" />
          <Button kind="primary" onClick={async () => {
            await api('/api/settings', { method: 'PATCH', body: { parts_markup_mode: mode, parts_markup_pct: Number(flat || 0), parts_markup_tiers: JSON.stringify(tiers()) } });
            toast('Parts pricing saved'); onSaved();
          }}>Save parts pricing</Button></div>
      </div>
    </Panel>
  );
}

function Colors({ current, onSaved }: { current: string; onSaved: () => void }) {
  const toast = useToast();
  const [pick, setPick] = useState(current);
  return (
    <Panel title="Look & colors">
      <div className="pad">
        <div className="theme-picks">
          {Object.entries(THEMES).map(([k, t]) => (
            <button key={k} type="button" className={'theme-pick' + (pick === k ? ' on' : '')} onClick={() => { setPick(k); applyTheme(k); }}>
              <span className="sw" style={{ background: `linear-gradient(160deg, ${t.navy2}, ${t.navy})` }}><i style={{ borderColor: t.accent, color: t.accent }}>+</i><em style={{ background: t.steel }} /></span>
              <b>{t.label}</b>
            </button>
          ))}
        </div>
        <p className="form-note">Click to try it — the whole app changes right away. Save to keep it for everyone in your shop.</p>
        <div className="actions"><Button kind="primary" disabled={pick === current} onClick={async () => { await api('/api/settings', { method: 'PATCH', body: { theme: pick } }); toast('Colors saved'); onSaved(); }}>Save colors</Button>
          {pick !== current && <Button onClick={() => { setPick(current); applyTheme(current); }}>Cancel</Button>}</div>
      </div>
    </Panel>
  );
}

function Trackers() {
  const { data } = useApi<Row[]>('/api/trackers');
  return (
    <Panel title="Samsara & Motive (fleet trackers)">
      <div className="pad">
        <p>Connect a fleet customer's Samsara or Motive with their API token — on <b>Customers → the customer → Fleet tracker</b>. Mileage updates by itself every 6 hours, trucks you don't have yet can be added, and you get an alert when a truck shows a new engine fault code.</p>
        {!data ? <Loading /> : data.length ? (
          <ul className="list small">{data.map(l => (
            <li key={l.id} onClick={() => navigate('/customers/' + l.customer_id)} style={{ cursor: 'pointer' }}>
              <span><b>{l.customer_name}</b> · {l.provider === 'samsara' ? 'Samsara' : 'Motive'} · {l.vehicles} trucks{l.open_faults ? <> · <Badge tone="red">{l.open_faults} fault codes</Badge></> : null}</span>
              <span className="muted right">{l.last_error ? <span className="danger">{l.last_error}</span> : l.last_sync ? 'synced ' + fmtTime(l.last_sync) : ''}</span>
            </li>))}</ul>
        ) : <p className="muted">No customers connected yet.</p>}
      </div>
    </Panel>
  );
}

function QuickBooks() {
  const toast = useToast();
  const [from, setFrom] = useState(today().slice(0, 8) + '01');
  const [to, setTo] = useState(today());
  const get = async (what: string, name: string) => {
    try {
      const r = await api<{ heads: string[]; rows: (string | number)[][] }>(`/api/export/quickbooks?what=${what}&from=${from}&to=${to}`);
      if (!r.rows.length) { toast('Nothing in those dates', 'err'); return; }
      downloadCsv(name, r.heads, r.rows);
    } catch (e) { toast((e as Error).message, 'err'); }
  };
  return (
    <Panel title="QuickBooks">
      <div className="pad">
        <p>Download files ready for <b>QuickBooks Online → Settings ⚙ → Import data</b>. (A live QuickBooks connection is coming later.)</p>
        <div className="qb-row"><Field label="From" name="qb_from" type="date" value={from} onChange={e => setFrom(e.target.value)} /><Field label="To" name="qb_to" type="date" value={to} onChange={e => setTo(e.target.value)} /></div>
        <div className="actions">
          <Button icon="download" onClick={() => void get('invoices', `quickbooks-invoices-${from}-to-${to}.csv`)}>Invoices</Button>
          <Button icon="download" onClick={() => void get('payments', `quickbooks-payments-${from}-to-${to}.csv`)}>Payments</Button>
          <Button icon="download" onClick={() => void get('customers', 'quickbooks-customers.csv')}>Customers</Button>
        </div>
      </div>
    </Panel>
  );
}

function YourData() {
  return (
    <Panel title="Your data & backups">
      <div className="pad">
        <p>Asena Bay copies your whole shop every night and keeps the copies for 90 days (plus one per month for a year), so your data can be put back to an earlier day if something goes wrong.</p>
        <p>You can also download <b>everything</b> — customers, trucks, work orders, invoices, parts, staff — as one file any time. It's yours.</p>
        <div className="actions"><a className="btn secondary" href="/api/export/all" download><Icon name="download" size={16} />Download all my data</a></div>
      </div>
    </Panel>
  );
}

/** Which inventory part is the grease for oil change / PM (empty = the first part with "grease" in its name). */
function GreasePart({ value }: { value: string }) {
  const { data } = useApi<Row[]>('/api/parts?q=grease');
  return (
    <label className="field"><span>Grease part</span>
      <select name="auto_grease_part" defaultValue={value} key={(data || []).length}>
        <option value="">Any part named "grease" (or a line to price)</option>
        {(data || []).map(p => <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
      </select></label>
  );
}

/** Shop TV: a secret link for a TV in the waiting room — trucks, jobs, who is on them. */
function ShopTvPanel() {
  const { data, reload } = useApi<{ link: string | null }>('/api/tv-link');
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const set = async (on: boolean) => {
    if (!on && !confirmAction('Turn the TV link off? The TV shows "turned off" until you make a new link.')) return;
    if (on && data?.link && !confirmAction('Make a new link? The TV with the old link stops working — open the new link on it.')) return;
    setBusy(true);
    try { await api('/api/tv-link', { body: { on } }); reload(); toast(on ? 'TV link ready — open it on the TV' : 'TV link turned off'); } catch (e) { toast((e as Error).message, 'err'); } finally { setBusy(false); }
  };
  return (
    <Panel title="📺 Shop TV">
      <div className="pad tv-set">
        <p className="muted">A screen for the drivers waiting: every truck in the shop — company, unit #, last 6 of the VIN — its jobs, who is working on them and when it's ready. No prices, no phone numbers. Updates by itself.</p>
        {!data ? <Loading /> : data.link ? <>
          <div className="tv-link"><input readOnly value={data.link} onFocus={e => e.target.select()} aria-label="TV link" />
            <Button onClick={async () => { try { await navigator.clipboard.writeText(data.link!); toast('Link copied'); } catch { toast('Select the link and copy it', 'err'); } }}>Copy</Button>
            <Button onClick={() => window.open(data.link!, '_blank')}>Open</Button></div>
          <p className="form-note">Open this link in the TV's web browser (Smart TV, Fire TV Stick, Chromecast with Google TV, or a laptop on HDMI). Anyone with the link sees the screen — make a new one if it gets out.</p>
          <div className="actions-row"><Button busy={busy} onClick={() => set(true)}>New link</Button><Button kind="ghost" busy={busy} onClick={() => set(false)}>Turn off</Button></div>
        </> : <Button kind="primary" busy={busy} onClick={() => set(true)}>Make the TV link</Button>}
      </div>
    </Panel>
  );
}
